'use client';

import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { MapPin, Users, Lightbulb, MessageSquare, TrendingUp, Shield } from 'lucide-react';
import { SECTION_HEADING_CLASS, SECTION_HEADING_STYLE } from './sectionHeading';

const differentiators = [
  {
    id: 'diff-local',
    icon: MapPin,
    code: 'WHY-01',
    title: 'Deep local market knowledge',
    description:
      'Hands-on experience across Europe and Australia means we understand the specific dynamics, regulations, and consumer behaviours that matter in your market.',
  },
  {
    id: 'diff-practical',
    icon: Lightbulb,
    code: 'WHY-02',
    title: 'Practical over theoretical',
    description:
      'We focus on what actually works for SMBs — not enterprise playbooks that require teams of 50. Every recommendation is actionable within your budget and capacity.',
  },
  {
    id: 'diff-cross',
    icon: Users,
    code: 'WHY-03',
    title: 'Cross-functional expertise',
    description:
      'Our team spans digital marketing, paid advertising, SEO, design, and business strategy — so you get integrated thinking, not siloed advice.',
  },
  {
    id: 'diff-comms',
    icon: MessageSquare,
    code: 'WHY-04',
    title: 'Clear, honest communication',
    description:
      'No jargon, no inflated promises. We tell you what is working, what is not, and what we recommend doing about it — transparently, every time.',
  },
  {
    id: 'diff-data',
    icon: TrendingUp,
    code: 'WHY-05',
    title: 'Data-informed decisions',
    description:
      'Every strategy recommendation is backed by your actual data and market evidence. We do not guess — we analyse and then act.',
  },
  {
    id: 'diff-trust',
    icon: Shield,
    code: 'WHY-06',
    title: 'Transparency and integrity',
    description:
      'Founded on the principles of transparency, integrity, and practicality. No hidden costs, no inflated reports, no unrealistic promises.',
  },
];

export default function WhyNoventiSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section data-ground id="about" className="section-rhythm m-ground" ref={ref}>
      <div className="m-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 lg:gap-24 items-start">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div style={{ width: 24, height: 1, background: 'rgba(var(--m-ink-rgb),0.3)' }} />
              <span
                className="font-mono text-[11px] uppercase tracking-widest"
                style={{ color: 'var(--m-muted)', letterSpacing: '0.22em' }}
              >
                Why Noventi Labs
              </span>
            </div>

            <h2 className={`${SECTION_HEADING_CLASS} mb-8`} style={SECTION_HEADING_STYLE}>
              <span className="sm:hidden">Why clients stay with us</span>
              <span className="hidden sm:inline">
                A team that genuinely
                <br />
                cares about your
                <br />
                business growth
              </span>
            </h2>

            <div
              style={{
                width: 48,
                height: 2,
                background: 'rgba(var(--m-ink-rgb),0.15)',
                marginBottom: 28,
              }}
            />

            <p
              className="leading-relaxed mb-5"
              style={{ color: 'var(--m-ink-5)', lineHeight: 1.75, fontSize: 14 }}
            >
              <span className="hidden sm:inline">
                At Noventi Labs, we are passionate about helping businesses achieve their fullest
                potential through strategic marketing and consulting services. Our dedicated team
                combines extensive local market knowledge with global best practices.
              </span>
              {/* Same facts, restructured. The desktop version opens with two
                  four-line paragraphs; on a phone that is a wall of text before
                  the first landmark, so the specifics become a scannable list. */}
              <span className="sm:hidden">
                We help small and medium-sized businesses grow, and we keep it practical.
              </span>
            </p>
            <ul className="grid gap-2 sm:hidden mb-8" style={{ listStyle: 'none', padding: 0 }}>
              {[
                ['Who we help', 'Hospitality, e-commerce and service businesses'],
                ['Where', 'Europe and Australia'],
                ['How we work', 'Simple, practical, tailored to what really works'],
              ].map(([label, value]) => (
                <li key={label} style={{ display: 'flex', flexDirection: 'column' }}>
                  <span
                    className="font-mono uppercase"
                    style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--m-ink-6)' }}
                  >
                    {label}
                  </span>
                  <span style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--m-ink-3)' }}>
                    {value}
                  </span>
                </li>
              ))}
            </ul>
            <p
              className="hidden sm:block leading-relaxed mb-10"
              style={{ color: 'var(--m-ink-5)', lineHeight: 1.75, fontSize: 14 }}
            >
              We focus on supporting small and medium-sized companies in the hospitality,
              e-commerce, and service industries across Europe and Australia. We keep things simple,
              practical, and tailored to what really works.
            </p>

            {/* Pull quote */}
            <div className="relative pl-6" style={{ borderLeft: '3px solid var(--m-ink)' }}>
              <div
                className="absolute -left-px top-0 bottom-0"
                style={{
                  width: 3,
                  background:
                    'linear-gradient(to bottom, var(--m-ink), rgba(var(--m-ink-rgb),0.1))',
                }}
              />
              <p
                className="text-sm leading-relaxed italic mb-3"
                style={{ color: 'var(--m-ink-3)', lineHeight: 1.75 }}
              >
                &quot;Our mission is to empower businesses through practical, results-driven
                marketing and consulting solutions — providing transparent, innovative, and
                personalised strategies tailored specifically to each client&apos;s needs.&quot;
              </p>
              <p
                className="font-mono text-[11px] uppercase tracking-widest"
                style={{ color: 'var(--m-muted-3)', letterSpacing: '0.18em' }}
              >
                — Noventi Labs Mission Statement
              </p>
            </div>
          </motion.div>

          {/* Right column — differentiator cards.

              On a phone these six stacked into 1,255px, which made this the
              tallest section on the page at 2.4 screens. They now sit in a
              horizontal snap row instead — the same native-scroll treatment
              Services and Insights already use further up the page, so it is a
              gesture a visitor has met twice before reaching here.

              Nothing is hidden and nothing shrank: every card keeps its full
              text at its full size, and a card is 85% of the frame so the next
              one always peeks past the edge, which is what tells a thumb there
              is more. Two columns would have been shorter still and would have
              meant 13px body text in a 155px column, undoing the legibility the
              mobile audit was written to fix. Above `sm` the grid is exactly as
              it was. */}
          <div
            className="flex snap-x snap-mandatory overflow-x-auto gap-px pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0"
            style={{
              background: 'rgba(var(--m-ink-rgb),0.06)',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {differentiators?.map((item, i) => (
              <motion.div
                key={item?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="p-5 transition-all duration-200 group cursor-default shrink-0 basis-[85%] snap-start sm:basis-auto"
                style={{ background: 'var(--m-bg)' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--m-surface-2)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--m-bg)';
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 36,
                      height: 36,
                      border: '1px solid rgba(var(--m-ink-rgb),0.08)',
                      background: 'rgba(var(--m-ink-rgb),0.03)',
                    }}
                  >
                    <item.icon
                      className="w-4 h-4"
                      style={{ color: 'var(--m-ink-4)', width: 15, height: 15 }}
                    />
                  </div>
                  <span
                    className="font-mono text-[8px] uppercase tracking-widest"
                    style={{ color: 'var(--m-muted-5)', letterSpacing: '0.14em' }}
                  >
                    {item.code}
                  </span>
                </div>
                <h3
                  className="font-bold uppercase mb-2 leading-tight"
                  style={{
                    fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                    fontSize: 14,
                    color: 'var(--m-ink)',
                    letterSpacing: '0.04em',
                  }}
                >
                  {item?.title}
                </h3>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: 'var(--m-ink-6)', lineHeight: 1.65, fontSize: 15 }}
                >
                  {item?.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
